/**
 * AuthContext — HttpOnly cookie-based session management.
 *
 * The JWT token lives exclusively in the browser's HttpOnly cookie
 * and is never accessible from JS. AuthContext stores only the user
 * object (for display purposes) and validates the session on page
 * reload by calling /auth/me.
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import type { PrequalificationStatus } from '@vedjegy/shared';

export interface AuthUser {
  id: string;
  email: string;
  role: string;
  roles: string[];
  companyId: string | null;
  companyName?: string | null;
  prequalificationStatus: PrequalificationStatus;
  isRenewal: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (user: AuthUser) => void;
  logout: () => Promise<void>;
  // Re-fetch /me and update stored user — call after JWT is re-issued (e.g. company switch)
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const USER_KEY = 'vedjegy_user';

const PRIVILEGED_ROLES = ['super_admin', 'tenant_admin', 'reviewer', 'editor'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]           = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On page load: validate the cookie by calling /me.
  // The sessionStorage user is only for optimistic fast rendering.
  useEffect(() => {
    const cached = sessionStorage.getItem(USER_KEY);
    if (cached) {
      try { setUser(JSON.parse(cached) as AuthUser); } catch { /* */ }
    }

    api.auth.me()
      .then((me) => {
        const roles = me.roles ?? (me.role ? [me.role] : ['company_user']);
        const isPrivileged = roles.some((r) => (PRIVILEGED_ROLES as readonly string[]).includes(r));
        const u: AuthUser = {
          id: me.id, email: me.email,
          role: me.role ?? roles[0] ?? 'company_user',
          roles,
          companyId: me.companyId,
          companyName: me.companyName ?? null,
          // Privileged users bypass preq gate entirely
          prequalificationStatus: isPrivileged ? 'passed' : (me.prequalificationStatus ?? 'none'),
          isRenewal: me.isRenewal ?? false,
        };
        setUser(u);
        sessionStorage.setItem(USER_KEY, JSON.stringify(u));
      })
      .catch(() => {
        // Cookie expired or missing — clear the cached user
        sessionStorage.removeItem(USER_KEY);
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  function login(newUser: AuthUser) {
    sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setUser(newUser);
  }

  async function logout() {
    try { await api.auth.logout(); } catch { /* */ }
    sessionStorage.removeItem(USER_KEY);
    setUser(null);
  }

  // Re-reads /me and updates stored user (used after JWT re-issue)
  async function refresh() {
    try {
      const me = await api.auth.me();
      const roles = me.roles ?? (me.role ? [me.role] : ['company_user']);
      const isPrivileged = roles.some((r) => (PRIVILEGED_ROLES as readonly string[]).includes(r));
      const u: AuthUser = {
        id: me.id, email: me.email,
        role: me.role ?? roles[0] ?? 'company_user',
        roles,
        companyId: me.companyId,
        companyName: me.companyName ?? null,
        prequalificationStatus: isPrivileged ? 'passed' : (me.prequalificationStatus ?? 'none'),
        isRenewal: me.isRenewal ?? false,
      };
      setUser(u);
      sessionStorage.setItem(USER_KEY, JSON.stringify(u));
    } catch { /* silently ignore — session might have ended */ }
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
